import { useMemo, useRef, useState } from "react";
import { CaretRightFilled, PauseOutlined } from "@ant-design/icons";
import {
  HiddenAudio,
  PlayButton,
  PlayerShell,
  TimeLabel,
  WaveBar,
  Waveform,
} from "./style";

const BAR_COUNT = 48;

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00";
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

interface AudioPlayerProps {
  src: string;
}

export default function AudioPlayer({ src }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Deterministic pseudo-random bar heights so the waveform is stable across
  // renders while still looking organic.
  const bars = useMemo(
    () =>
      Array.from({ length: BAR_COUNT }, (_, i) => {
        const seed = Math.sin((i + 1) * 12.9898) * 43758.5453;
        const r = seed - Math.floor(seed);
        return 0.22 + r * 0.78;
      }),
    [],
  );

  const progress = duration > 0 ? currentTime / duration : 0;

  function togglePlay() {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      void audio.play();
    } else {
      audio.pause();
    }
  }

  function seekToBar(index: number) {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    audio.currentTime = (index / BAR_COUNT) * duration;
    setCurrentTime(audio.currentTime);
  }

  const displayTime = currentTime > 0 ? currentTime : duration;

  return (
    <PlayerShell align="center" gap={12}>
      <PlayButton
        aria-label={playing ? "Pause recording" : "Play recording"}
        icon={playing ? <PauseOutlined /> : <CaretRightFilled />}
        onClick={togglePlay}
      />
      <Waveform align="center" gap={0}>
        {bars.map((height, i) => (
          <WaveBar
            key={i}
            $height={height}
            $played={i / BAR_COUNT <= progress}
            onClick={() => seekToBar(i)}
          />
        ))}
      </Waveform>
      <TimeLabel>{formatTime(displayTime)}</TimeLabel>
      <HiddenAudio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setCurrentTime(0);
        }}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
      />
    </PlayerShell>
  );
}
